import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import {
  DocumentPreviewSection,
  LineItemsSection,
} from "@/accounts-payable/common";
import { useGetInvoiceAgentRuns } from "@/accounts-payable/query";
import type { ApInvoice } from "@/accounts-payable/types";
import {
  getActiveRun,
  getLatestCompletedRun,
  runsInFlightRefetchInterval,
} from "@/accounts-payable/utils";
import { AgentActionsSection } from "./agent-actions-section";
import { InvoiceActivitySection } from "./invoice-activity-section";
import { InvoiceFieldsSection } from "./invoice-fields-section";

/**
 * The invoice detail content — extracted fields, the document, the agent
 * actions for this invoice, and the activity log.
 */
export const InvoiceDetailBody = ({
  invoice,
  businessId,
  onViewAgentReview,
}: {
  invoice: ApInvoice;
  businessId: string | null;
  onViewAgentReview?: (invoiceId: string) => void;
}) => {
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useGetInvoiceAgentRuns(
    businessId,
    invoice.id,
    runsInFlightRefetchInterval,
  );
  const activeRun = getActiveRun(runs);
  const run = getLatestCompletedRun(runs);

  return (
    <Stack gap="lg" fullWidth className="px-6 py-6">
      {/* Details — no card and no heading: they lead the panel under the
          header; later sections are split by separators, as in Spiffs. */}
      <InvoiceFieldsSection invoice={invoice} businessId={businessId} />
      <Separator />

      {/* Lines and their pricebook matches, each correctable by hand. */}
      {invoice.invoice_items.length > 0 && (
        <>
          <LineItemsSection
            items={invoice.invoice_items}
            businessId={businessId}
            documentId={invoice.id}
            documentType="invoice"
            agentRunId={invoice.agent_run_id}
          />
          <Separator />
        </>
      )}

      {/* Invoice document — the one framed element: it's a picture of a page. */}
      <DocumentPreviewSection document={invoice} />
      <Separator />

      {/* Keyed so the Resolved toggle resets when another invoice opens. */}
      <AgentActionsSection
        key={invoice.id}
        document={invoice}
        run={run}
        activeRun={activeRun}
        loading={runsLoading}
        error={runsError}
        onViewAgentReview={onViewAgentReview}
      />

      <Separator />

      {/* Activity */}
      <InvoiceActivitySection invoice={invoice} />
    </Stack>
  );
};
